// @ts-nocheck

import React, {useState, useEffect} from 'react';
import { useParams } from 'react-router-dom';
import CodeEditor from '@uiw/react-textarea-code-editor';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button } from '@mui/material';
import ReactAnsi from 'react-ansi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import Grid from '@mui/material/Grid';
import { getChallenge, executeChallenge, downloadChallenge } from "../../api/Api";
import DehazeIcon from '@mui/icons-material/Dehaze';
import Code from "./Code";
import LinearProgress from '@mui/material/LinearProgress';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FlagIcon from '@mui/icons-material/Flag';
import fileDownload from 'js-file-download';
import ModalComplete from "./ModalComplete";
import { useNavigate } from "react-router-dom";
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';
import { useColors, useColorMode } from '../../theme/ColorModeContext';
import { categoryToText } from '../../utils/Category';
import { getAcademyTrackForCategory } from '../Academy/skillTreeData';
import { getCodeLanguageFromFileName } from '../../utils/codeLanguage';
import DiscordCTA from '../../shared/DiscordCTA';

function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
}

interface PropsChallenge {
    token: string;
}

interface File {
    name: string;
    content: string;
    editable: boolean;
    solution: boolean;
}
interface Challenge {
    id: string;
    name: string;
    description: string;
    mission: string;
    solved: boolean;
    category: string;
}

const Challenge = (props: PropsChallenge) => {
    const colors = useColors();
    const { mode } = useColorMode();
    const [challenge, setChallenge] = useState<Challenge|null>(null);
    const token = props.token;
    const { id } = useParams();
    const navigate = useNavigate();

    const [index, setIndex] = useState(0);
    const [code, setCode] = useState("");
    const [filetype, setFiletype] = useState<string|undefined>("sol");
    const [editDisabled, setEditDisabled] = useState(true);
    const [files, setFiles] = useState<File[]>([]);
    const [stdout, setStdout] = useState("");
    const [stderr, setStderr] = useState("");
    const [processing, setProcessing] = useState(false);
    const [processingAction, setProcessingAction] = useState<string>("");
    const [slowDown, setSlowDown] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [lastErrcode, setLastErrcode] = useState<number | null>(null);

    const [metaDescription, setMetaDescription] = useState("");
    const [metaIdx, setMetaIdx] = useState(0);
    const [modalCompletedOpen, setModalCompletedOpen] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const data = await getChallenge(token, id);

            if(!token && (
                data.category !== "introduction" &&
                data.category !== "access-control" &&
                data.category !== "missions"
            )) {
                navigate("/register");
                return;
            }

            setChallenge(data);
            setFiles(data.files.concat(data.tests));

            let filetype = getCodeLanguageFromFileName(data.files[0].name);
            setFiletype(filetype)
            setCode(data.files[0].content);
            setEditDisabled(!data.files[0].editable);
            setMetaDescription(data.mission);
        }
        fetchData();
    }, []);

    const handleChange = (e: any, idx: number) => {
      setIndex(idx);
      if(challenge) {
        let filetype = getCodeLanguageFromFileName(files[idx].name);
        setFiletype(filetype);
        setCode(files[idx].content);
        setEditDisabled(!files[idx].editable);
      }
    };

    const handleChangeMeta = (event: React.SyntheticEvent, idx: number) => {
        setMetaIdx(idx);
        if(idx === 0) setMetaDescription(challenge.mission);
        else if(idx === 1) setMetaDescription(challenge.description);
    };

    const updateCode = (idx: number, content: string) => {
        let items = [...files];
        let item = {...items[idx]};
        item.content = content;
        items[idx] = item;
        setFiles(items);
    }

    const execute = async (action: string) => {
        if(token === "") {
            navigate("/register");
            return;
        }

        for(let i=0; i<files.length; i++) {
            if(files[i].solution) {
                setProcessing(true);
                setProcessingAction(action);
                const response = await executeChallenge(token, action, id, files[i].content);

                if(response !== null) {
                    if(response.status === 200) {
                        setStderr(response.data["stderr"]);
                        setStdout(response.data["stdout"]);
                        setLastErrcode(response.data["errcode"]);
                        if(response.data["errcode"] === 0) {
                            if(challenge) {
                                setModalCompletedOpen(true);
                                challenge.solved = true;
                                setChallenge(challenge);
                            }
                        }
                        if(slowDown) setSlowDown(false);
                    }
                    if(response.status === 429) setSlowDown(true);
                }
                setProcessing(false);
                setProcessingAction("");
            }
        }
    }

    const download = async () => {
        if(token === "") {
            navigate("/register");
            return;
        }
        const response = await downloadChallenge(token, id);
        if(response !== null) {
            fileDownload(response, "app.zip");
        }
    }

    function Image(props) {
        return <img {...props} style={{maxWidth: '100%'}} />
    }

    if(!challenge) {
        return <Box sx={{ textAlign: 'center', marginTop: '120px' }}><CircularProgress size={60} /></Box>
    }

    let categoryName = categoryToText(challenge.category);
    let breadcrumbSegments = [
        {
            label: getAcademyTrackForCategory(challenge.category).title,
            to: `/academy/${getAcademyTrackForCategory(challenge.category).slug}`,
        },
        { label: categoryName, to: `/academy/${challenge.category}` },
        { label: challenge.name },
    ];
    if(challenge.category === "missions" || challenge.category === "season1") {
        breadcrumbSegments = [
            { label: categoryName, to: '/missions' },
            { label: challenge.name },
        ];
    }

    // Console accent color based on result
    const consoleAccent = lastErrcode === null
        ? colors.border.default
        : lastErrcode === 0
            ? colors.accent.green
            : colors.accent.red;
    const consoleGlow = lastErrcode === null
        ? 'none'
        : lastErrcode === 0
            ? `0 0 8px ${colors.accent.greenGlow}`
            : `0 0 8px ${colors.accent.redGlow}`;

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <CyberpunkTitle
                        segments={breadcrumbSegments}
                        mb="0"
                    />
                </Box>
                {challenge.solved && (
                    <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 16px',
                        borderRadius: '20px',
                        backgroundColor: colors.accent.greenGlow,
                        border: `1px solid ${colors.accent.green}40`,
                        mt: '18px',
                        flexShrink: 0,
                    }}>
                        <CheckCircleIcon sx={{ color: colors.accent.green, fontSize: '18px' }} />
                        <Typography variant="body2" sx={{ color: colors.accent.green, fontWeight: 600, fontFamily: '"Orbitron", sans-serif' }}>
                            SOLVED
                        </Typography>
                    </Box>
                )}
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={fullscreen ? 12 : 7}>
                    <GlowCard glowOnHover={false} sx={{ padding: '0' }}>
                        <Grid container>
                            <Grid item xs={11}>
                                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                  <Tabs value={index} onChange={handleChange} variant="scrollable" scrollButtons="auto" sx={{
                                    '& .MuiTab-root': {
                                        borderRadius: '6px 6px 0 0',
                                        minHeight: '42px',
                                        padding: '8px 16px',
                                        fontSize: '0.82rem',
                                    },
                                    '& .MuiTab-root.Mui-selected': {
                                        backgroundColor: colors.bg.tertiary,
                                    },
                                    '& .MuiTabs-indicator': {
                                        boxShadow: 'none',
                                    },
                                    '& .MuiTabScrollButton-root': {
                                        color: colors.accent.cyan,
                                        opacity: 1,
                                        '&.Mui-disabled': { opacity: 0.2 },
                                    },
                                  }}>
                                    {files.map((file, idx) => (
                                        <Tab key={idx} label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {file.editable
                                                    ? <EditNoteIcon sx={{ fontSize: '18px' }} />
                                                    : <DehazeIcon sx={{ fontSize: '16px' }} />
                                                }
                                                {file.name}
                                                {file.editable && <Box sx={{
                                                    width: 5,
                                                    height: 5,
                                                    borderRadius: '50%',
                                                    backgroundColor: colors.accent.cyan,
                                                    flexShrink: 0,
                                                }} />}
                                            </Box>
                                        }
                                            {...a11yProps(idx)}
                                        />
                                    ))}
                                  </Tabs>
                                </Box>
                            </Grid>
                            <Grid item xs={1} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: '8px', paddingRight: '12px', gap: '2px' }}>
                                {fullscreen ?
                                    <CloseFullscreenIcon
                                        sx={{
                                            fontSize: '20px',
                                            color: colors.text.secondary,
                                            cursor: 'pointer',
                                            transition: 'color 0.2s ease',
                                            '&:hover': { color: colors.accent.cyan },
                                        }}
                                        onClick={() => setFullscreen(false)}
                                    />
                                :
                                    <FullscreenIcon
                                        sx={{
                                            fontSize: '22px',
                                            color: colors.text.secondary,
                                            cursor: 'pointer',
                                            transition: 'color 0.2s ease',
                                            '&:hover': { color: colors.accent.cyan },
                                        }}
                                        onClick={() => setFullscreen(true)}
                                    />
                                }
                                <Typography sx={{
                                    fontSize: '10px',
                                    color: colors.text.muted,
                                    fontFamily: '"Orbitron", sans-serif',
                                    fontWeight: 600,
                                    lineHeight: 1,
                                    userSelect: 'none',
                                }}>
                                    {files.length}f
                                </Typography>
                            </Grid>
                        </Grid>

                        {/* Subtle separator between tabs and editor */}
                        <Box sx={{ height: '1px', background: colors.border.default }} />

                        <CodeEditor
                          value={code}
                          language={filetype}
                          onChange={(e) => updateCode(index, e.target.value)}
                          padding={15}
                          data-color-mode={mode}
                          disabled={editDisabled}
                          style={{
                            fontSize: 15,
                            fontFamily: '"Share Tech Mono", ui-monospace, SFMono-Regular, SF Mono, Consolas, Liberation Mono, Menlo, monospace',
                            backgroundColor: colors.bg.tertiary,
                            borderBottom: `1px solid ${colors.border.subtle}`,
                            minHeight: '300px',
                          }}
                        />
                    </GlowCard>

                    <Box sx={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                        <Button
                            variant="contained"
                            sx={{
                                width: '150px',
                                position: 'relative',
                                background: colors.bg.surface,
                                border: `1px solid ${colors.border.strong}`,
                                color: colors.text.primary,
                                '&:hover': {
                                    background: colors.bg.tertiary,
                                    borderColor: colors.accent.cyanDim,
                                    boxShadow: 'none',
                                    transform: 'none',
                                },
                                '&.Mui-disabled': {
                                    background: colors.bg.tertiary,
                                    border: `1px solid ${colors.border.subtle}`,
                                    color: colors.text.muted,
                                },
                            }}
                            onClick={() => execute("compile")}
                            disabled={processing}
                        >
                            {processing && processingAction === 'compile' ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <CircularProgress size={16} sx={{ color: 'inherit' }} />
                                    Building...
                                </Box>
                            ) : 'Build'}
                        </Button>
                        <Button
                            variant="contained"
                            sx={{
                                width: '150px',
                                position: 'relative',
                                background: colors.bg.surface,
                                border: `1px solid ${colors.border.strong}`,
                                color: colors.text.primary,
                                '&:hover': {
                                    background: colors.bg.tertiary,
                                    borderColor: colors.accent.cyanDim,
                                    boxShadow: 'none',
                                    transform: 'none',
                                },
                                '&.Mui-disabled': {
                                    background: colors.bg.tertiary,
                                    border: `1px solid ${colors.border.subtle}`,
                                    color: colors.text.muted,
                                },
                            }}
                            onClick={() => execute("test")}
                            disabled={processing}
                        >
                            {processing && processingAction === 'test' ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <CircularProgress size={16} sx={{ color: 'inherit' }} />
                                    Executing...
                                </Box>
                            ) : 'Execute'}
                        </Button>
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                            variant="outlined"
                            color="warning"
                            sx={{ width: '150px' }}
                            onClick={download}
                        >
                            Download
                        </Button>
                    </Box>

                    {slowDown && (
                        <Typography sx={{
                            color: colors.accent.orange,
                            fontWeight: 600,
                            marginTop: '8px',
                            animation: 'shake 0.5s ease-in-out',
                        }}>
                            Slow Down!
                        </Typography>
                    )}

                    {processing ?
                         <LinearProgress sx={{ marginTop: '12px', borderRadius: '4px' }} />
                         :
                         <Box sx={{ height: '4px', marginTop: '12px' }} />
                    }

                    {/* Console with color-coded border */}
                    <Box sx={{ marginTop: '8px' }}>
                        {/* Thin accent bar above console */}
                        <Box sx={{
                            height: '2px',
                            borderRadius: '2px 2px 0 0',
                            background: lastErrcode === null
                                ? colors.border.default
                                : lastErrcode === 0
                                    ? colors.accent.green
                                    : colors.accent.red,
                            transition: 'background 0.3s ease',
                        }} />
                        <ReactAnsi style={{ fontSize: 13 }}
                            bodyStyle={{
                                backgroundColor: '#0f1420',
                                border: `1px solid ${consoleAccent}`,
                                borderTop: 'none',
                                borderRadius: '0 0 8px 8px',
                                boxShadow: consoleGlow,
                                transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                            }}
                            log={stderr ? stderr : stdout}
                        />
                        {!challenge.solved && (
                            <Box sx={{ marginTop: '12px', textAlign: 'center' }}>
                                <DiscordCTA variant="inline" placement="challenge-console" />
                            </Box>
                        )}
                    </Box>
                </Grid>

                <Grid item xs={fullscreen ? 12 : 5}>
                    <GlowCard glowOnHover={false} sx={{ padding: '0' }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                             <Tabs value={metaIdx} onChange={handleChangeMeta}>
                                {challenge.mission && <Tab key={0} label="Mission" icon={<FlagIcon />} iconPosition="start" {...a11yProps(0)} />}
                                {challenge.description && <Tab key={1} label="Study Material" icon={<MenuBookIcon />} iconPosition="start" {...a11yProps(1)} />}
                             </Tabs>
                        </Box>
                        <Box sx={{ padding: '16px 20px', lineHeight: '22px', overflow: 'hidden' }}>
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    code: (node) => <Code node={node} />,
                                    // The parent clips overflow, so wide tables scroll inside their own box.
                                    table: ({node, ...props}) => (
                                        <Box sx={{ overflowX: 'auto', margin: '16px 0' }}>
                                            <Box
                                                component="table"
                                                {...props}
                                                sx={{
                                                    borderCollapse: 'collapse',
                                                    fontSize: '14px',
                                                    '& th, & td': {
                                                        border: `1px solid ${colors.border.default}`,
                                                        padding: '6px 12px',
                                                        verticalAlign: 'top',
                                                    },
                                                    '& th': {
                                                        background: colors.bg.tertiary,
                                                        color: colors.text.primary,
                                                        fontWeight: 600,
                                                    },
                                                    '& tbody tr:nth-of-type(even)': {
                                                        background: colors.bg.secondary,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    ),
                                }}
                            >
                                {metaDescription}
                            </ReactMarkdown>
                        </Box>
                    </GlowCard>
                </Grid>
            </Grid>
            <ModalComplete open={modalCompletedOpen} token={token} setOpen={setModalCompletedOpen} />
        </PageContainer>
    )
};

export default Challenge;
